import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ModalDialog } from '../../../../components/ModalDialog';
import { useAgreements } from '../../../../hooks/api/useAgreements';
import { Journals } from '../../../../types/Journals.types';
import { MasterlistJournalCheck } from '../../../../types/masterlistCheck.types';
import { ErrorObject } from '../../../../types/errorhandling.types';
import { DeleteAgreementModal } from '../agreement/DeleteAgreementModal';
import { MasterlistCheckSection } from './MasterlistCheckSection';
import { MoveJournalModal } from './MoveJournalModal';
import { MultiSelectChips } from '../../../../components/inputs/MultiSelectChips';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { AccessibleSelect } from '../../../../components/inputs/AccessibleSelect';
import { AccessibleTextArea } from '../../../../components/inputs/AccessibleTextArea';
import { PaymentFields } from '../agreement/fields/PaymentFields';
import { StyledInputLabel } from '../../../../components/inputs/InputLabel';

export const JournalModal = ({
  journal,
  open,
  handleClose,
  editMode,
  agreementName,
  source,
  masterlistCheck,
}: {
  journal: Journals | undefined;
  open: boolean;
  handleClose: () => void;
  editMode: boolean;
  agreementName: string;
  source?: string;
  /** Shown as a collapsible section; only passed while the check is on. */
  masterlistCheck?: MasterlistJournalCheck;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { impersonationLock } = useAuthContext();

  const { addJournal, editJournal } = useAgreements();

  const [journals, setJournals] = useState<Journals[]>();
  const [paymentOptions, setPaymentOptions] = useState<string[]>([]);
  const [licenseOption, setLicenseOption] = useState<string[]>([]);
  const [publishingModelOptions, setPublishingModelOptions] = useState<
    string[]
  >([]);

  const labelId = 'licenses-label-journal-modal';

  interface FormDataType {
    name: string;
    imprint: string;
    publishing_model: string;
    issn_e: string;
    issn_p: string;
    payment: string;
    subjects: string;
    extra_info: string;
    licenses: string[];
    url: string;
  }

  const formData: FormDataType = {
    name: '',
    imprint: '',
    publishing_model: '',
    issn_e: '',
    issn_p: '',
    payment: '',
    subjects: '',
    extra_info: '',
    licenses: [],
    url: '',
  };

  const { agreementId } = useParams<{ agreementId: string }>();

  const [requestBody, setRequestBody] = useState<FormDataType>(formData);
  const {
    name,
    imprint,
    publishing_model: publishingModel,
    issn_e: issne,
    issn_p: issnp,
    payment,
    subjects: subject,
    extra_info: extraInfo,
    licenses,
    url,
  } = requestBody;

  const [changesMade, setChangesMade] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>();
  const [errors, setErrors] = useState<ErrorObject | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { fetchPayments, fetchLicenses, fetchPublishingModels } =
    useAgreements();

  useEffect(() => {
    const fetchData = async () => {
      const selectableLicenses = await fetchLicenses();
      setLicenseOption(selectableLicenses);
      const selectablePublishingModels = await fetchPublishingModels();
      setPublishingModelOptions(selectablePublishingModels);
    };
    setJournals(journals);
    fetchPayments().then((res) => setPaymentOptions(res));

    fetchData().catch((err) => console.log(err));
  }, [journals, fetchPayments, fetchLicenses, fetchPublishingModels]);

  useEffect(() => {
    if (!open) return;
    setRequestBody({
      name: journal?.name ?? '',
      imprint: journal?.imprint ?? '',
      publishing_model: journal?.publishing_model ?? '',
      issn_e: journal?.issn_e ?? '',
      issn_p: journal?.issn_p ?? '',
      payment: journal?.payment ?? '',
      subjects: journal?.subjects ?? '',
      extra_info: journal?.extra_info ?? '',
      licenses: journal?.licenses ?? [],
      url: journal?.url ?? '',
    });
    setChangesMade(false);
  }, [open, journal]);

  const onCloseHandler = () => {
    setError(undefined);
    setErrors(undefined);
    handleClose();
  };

  const requiredFieldsMissing = useMemo(() => {
    return (
      !requestBody.name ||
      !requestBody.publishing_model ||
      requestBody.licenses.length === 0 ||
      !requestBody.payment ||
      !requestBody.imprint ||
      !requestBody.url
    );
  }, [requestBody]);

  const handleChange = (event: {
    target: { name?: string; value: unknown };
  }) => {
    const { name, value } = event.target;
    setError(undefined);
    setErrors(undefined);
    setRequestBody((prev) => ({ ...prev, [name ?? '']: value }));
    if (!changesMade) {
      setChangesMade(true);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(undefined);
    setErrors(undefined);
    setIsLoading(true);

    try {
      if (editMode) {
        if (!journal?._id) {
          setError('Journal ID is required for editing');
          setIsLoading(false);
          return;
        }
        await editJournal(requestBody, agreementId!, journal._id);
      } else {
        await addJournal(requestBody, agreementId!);
      }
      setChangesMade(false);
      onCloseHandler();
      navigate(0);
    } catch (err: unknown) {
      const error = err as { response?: { data?: ErrorObject } };
      if (error.response?.data) {
        setErrors(error.response.data);
      } else {
        setError(
          'Something went wrong. Please look through the fields and try again.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalDialog
      open={open}
      onClose={onCloseHandler}
      title={
        editMode ? `Edit journal - ${agreementName}` : 'Create new journal'
      }
      subtitle={t('agreements.asteriskRequired') as string}
      error={error}
      errors={errors}
      actions={
        <>
          {editMode && (
            <Box sx={{ display: 'flex', gap: 12, mr: { sm: 'auto' } }}>
              {source !== 'move' && (
                <MoveJournalModal
                  journalId={journal!._id!}
                  agreementId={agreementId!}
                  journalUrl={url}
                  onSuccess={() => {
                    onCloseHandler();
                  }}
                  disabled={impersonationLock}
                />
              )}
              <DeleteAgreementModal
                id={agreementId!}
                journalId={journal?._id}
                journalMode
              />
            </Box>
          )}
          <Button variant="default" onClick={onCloseHandler}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="journal-form"
            disabled={
              (!changesMade && editMode) ||
              requiredFieldsMissing ||
              impersonationLock ||
              isLoading
            }
          >
            {isLoading && (
              <CircularProgress
                size={20}
                sx={{
                  color: 'white',
                  marginRight: 1,
                }}
              />
            )}
            {isLoading
              ? editMode
                ? 'Saving...'
                : 'Creating...'
              : editMode
                ? t('agreements.save')
                : 'Create'}
          </Button>
        </>
      }
    >
      <form id="journal-form" onSubmit={handleSubmit}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          {editMode && journal && masterlistCheck && (
            <MasterlistCheckSection check={masterlistCheck} journal={journal} />
          )}
          <Box>
            <StyledInputLabel htmlFor="journal-name">
              {t('agreements.journal')} <span aria-hidden="true">*</span>
            </StyledInputLabel>
            <TextField
              fullWidth
              required
              id="journal-name"
              name="name"
              value={name}
              onChange={(e) => handleChange(e)}
              disabled={impersonationLock}
            />
          </Box>

          <Box>
            <StyledInputLabel htmlFor="imprint">
              {t('agreements.imprint')} <span aria-hidden="true">*</span>
            </StyledInputLabel>
            <TextField
              fullWidth
              required
              id="imprint"
              name="imprint"
              value={imprint}
              onChange={(e) => handleChange(e)}
              disabled={impersonationLock}
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: '8px',
              alignItems: 'flex-start',
            }}
          >
            <PaymentFields
              payment={payment}
              paymentOptions={paymentOptions ?? []}
              onPaymentChange={(e) => handleChange(e)}
              disabled={impersonationLock}
              showPaymentUrl={false}
            />

            <Box sx={{ pt: '4px' }}>
              <StyledInputLabel id={labelId} htmlFor="licenses-input">
                License Options <span aria-hidden="true">*</span>
              </StyledInputLabel>
              <MultiSelectChips
                id="licenses"
                name="licenses"
                labelId={labelId}
                value={licenses}
                options={(licenseOption ?? []).map((option) => ({
                  value: option,
                  label: option,
                }))}
                disabled={impersonationLock}
                onChange={handleChange}
              />
            </Box>
          </Box>

          <Box>
            <AccessibleSelect
              id="publishing_model"
              label={
                <>
                  Publishing Model <span aria-hidden="true">*</span>
                  <span className="sr-only">required</span>
                </>
              }
              name="publishing_model"
              value={
                publishingModelOptions?.includes(publishingModel)
                  ? publishingModel
                  : ''
              }
              onChange={(e) => handleChange(e)}
              disabled={impersonationLock}
              fullWidth
              required
            >
              {publishingModelOptions?.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              )) ?? []}
            </AccessibleSelect>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: '8px',
            }}
          >
            <Box>
              <StyledInputLabel htmlFor="issn_e">ISSN-E</StyledInputLabel>
              <TextField
                fullWidth
                id="issn_e"
                name="issn_e"
                value={issne}
                onChange={(e) => handleChange(e)}
                disabled={impersonationLock}
              />
            </Box>

            <Box>
              <StyledInputLabel htmlFor="issn_p">ISSN-P</StyledInputLabel>
              <TextField
                fullWidth
                id="issn_p"
                name="issn_p"
                value={issnp}
                onChange={(e) => handleChange(e)}
                disabled={impersonationLock}
              />
            </Box>
          </Box>

          <Box>
            <StyledInputLabel id="subjects-label" htmlFor="subjects">
              {t('agreements.subject')}
            </StyledInputLabel>
            <TextField
              fullWidth
              id="subjects"
              name="subjects"
              value={subject}
              onChange={(e) => handleChange(e)}
              disabled={impersonationLock}
              inputProps={{
                'aria-labelledby': 'subjects-label',
              }}
            />
          </Box>

          <Box>
            <AccessibleTextArea
              id="extra_info"
              label={t('agreements.extraInfo', 'Extra information')}
              name="extra_info"
              value={extraInfo}
              onChange={(e) => handleChange(e)}
              disabled={impersonationLock}
              maxLength={400}
              rows={4}
            />
          </Box>

          <Box>
            <StyledInputLabel htmlFor="url">
              {t('agreements.journalURL')} <span aria-hidden="true">*</span>
              <span className="sr-only">required</span>
            </StyledInputLabel>
            <TextField
              required
              inputProps={{ pattern: 'http[s]?://.*' }}
              type="url"
              placeholder="https://www.example.com"
              fullWidth
              id="url"
              name="url"
              value={url}
              onChange={(e) => handleChange(e)}
              disabled={impersonationLock}
            />
          </Box>
        </Box>
      </form>
    </ModalDialog>
  );
};
